import type { FilterOption } from 'bundles/enterprise-ui/components/PopoverFilter';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

/**
 * Sentinel `sentBy` value meaning "no author filter". Any non-empty author
 * name from the dataset is a valid specific selection.
 */
export const SENT_BY_ALL = 'all';

/**
 * The two screens that can open the Preview screen inside the message modal.
 * Preview's Back button returns to whichever one launched it.
 */
export const PREVIEW_RETURN_SCREEN = {
  REVIEW: 'review',
  MANUAL: 'manual',
} as const;

export type PreviewReturnScreen = (typeof PREVIEW_RETURN_SCREEN)[keyof typeof PREVIEW_RETURN_SCREEN];

/**
 * Active filter selections for the Messaging landing page's Sent Messages
 * table. Each key corresponds to one of the three filter triggers rendered
 * by the `FilterBar` and matches an option `value` returned by the option
 * factories in this module.
 */
export type MessagingFilters = {
  /**
   * Selected sender. `'all'` means no filter; any other value is a literal
   * author name to match against `SentMessageRow.sentByName`.
   */
  sentBy: string;
  /** How often the message is sent — one-time vs. recurring/automated. */
  frequency: 'all' | 'one_time' | 'automated';
  /** Time window over which messages are shown. */
  timePeriod: '1_week' | '15_days' | '30_days' | '6_months' | '1_year';
};

/**
 * Initial filter state for the Messaging landing page. Also used as the
 * fallback when an individual filter is cleared by the user.
 */
export const DEFAULT_MESSAGING_FILTERS: MessagingFilters = {
  sentBy: SENT_BY_ALL,
  frequency: 'all',
  timePeriod: '30_days',
};

/**
 * Builds the **Sent by** option list from the supplied authors. Exposed as a
 * function because the labels use `_t()`, which must be invoked at render
 * time so locale changes propagate.
 */
export const getSentByOptions = (authors: string[]): FilterOption[] => [
  { value: SENT_BY_ALL, label: _t('All') },
  ...authors.map((name) => ({ value: name, label: name })),
];

/**
 * Options for the **Frequency** filter. Mirrors the values rendered by
 * `MessageFrequencyBadge`.
 *
 * @see Factory rationale on {@link getSentByOptions}.
 */
export const getFrequencyOptions = (): FilterOption[] => [
  { value: 'all', label: _t('All') },
  { value: 'one_time', label: _t('One time') },
  { value: 'automated', label: _t('Auto-send') },
];

/**
 * Options for the **Time period** filter. Listed in ascending chronological
 * order so the dropdown reads naturally top-to-bottom.
 *
 * @see Factory rationale on {@link getSentByOptions}.
 */
export const getTimePeriodOptions = (): FilterOption[] => [
  { value: '1_week', label: _t('1 week') },
  { value: '15_days', label: _t('15 days') },
  { value: '30_days', label: _t('30 days') },
  { value: '6_months', label: _t('6 months') },
  { value: '1_year', label: _t('1 year') },
];

/**
 * Number of days each `timePeriod` value represents. Used by
 * `filterSentMessages` to compute the cutoff window.
 */
export const TIME_PERIOD_DAYS: Record<MessagingFilters['timePeriod'], number> = {
  '1_week': 7,
  '15_days': 15,
  '30_days': 30,
  '6_months': 183,
  '1_year': 365,
};
